<template>
  <div class="search-result-container">
    <div v-if="!hasResult && !loading" class="center">
      <EmptySearch alt="search image" />
      <FriedH2 class="search-empty-title">{{ $t('could-not-find-anything') }}</FriedH2>
      <FriedParagraph class="search-empty-tip">{{ $t('try-a-different-phrase') }}</FriedParagraph>
    </div>
    <template v-else>
      <div :class="{ 'hidden-sm-and-down': documents.length === 0 }" class="result-documents">
        <FriedH4 class="section-title">{{ $t('Documents') }}</FriedH4>
        <table class="search-result-table">
          <colgroup>
            <col class="document-name" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>
                <FriedH5>{{ $t('name') }}</FriedH5>
              </th>
              <th>
                <FriedH5>{{ $t('status') }}</FriedH5>
              </th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr v-for="index in 10" :key="index">
              <td>
                <FriedSkeleton :type="SkeletonType.H5" />
              </td>
              <td>
                <FriedSkeleton :type="SkeletonType.StatusBadge" />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="(document, index) in documents"
              :id="document.id"
              :key="index"
              :class="{ highlighted: isItemHighlighted(SearchResultType.Documents, index) }"
              @click="selectDocument(document)"
            >
              <td :data-external="`topbar-search-doc:${document.name}-id:${document.id}-item`">
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ getNormalizedCellContent(document.name) }}
                </FriedParagraph>
              </td>
              <td>
                <FriedStatusBadge
                  :data-external="`topbar-search-doc:${document.name}-id:${document.id}-status`"
                  :text="getDocumentStatusTranslation(document.status)"
                  :status-type="getDocumentStatusType(document.status)"
                  :border="getDocumentHasBadgeBorder(document.status)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="result-seperator" />
      <div :class="{ 'hidden-sm-and-down': contacts.length === 0 }" class="result-contacts">
        <FriedH4 class="section-title">{{ $t('contacts') }}</FriedH4>
        <table class="search-result-table">
          <colgroup>
            <col class="contact-first-name" />
            <col class="contact-last-name" />
            <col class="contact-email" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <FriedH5>{{ $t('first-name') }}</FriedH5>
              </th>
              <th>
                <FriedH5>{{ $t('last-name') }}</FriedH5>
              </th>
              <th>
                <FriedH5>{{ $t('email') }}</FriedH5>
              </th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr v-for="index in 10" :key="index">
              <td>
                <FriedSkeleton style="width: 80%" :type="SkeletonType.H5" />
              </td>
              <td>
                <FriedSkeleton :type="SkeletonType.H5" />
              </td>
              <td>
                <FriedSkeleton :type="SkeletonType.H5" />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="(contact, index) in contacts"
              :id="contact.id"
              :key="index"
              :class="{ highlighted: isItemHighlighted(SearchResultType.Contacts, index) }"
              @click="selectContact(contact)"
            >
              <td>
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ getNormalizedCellContent(contact.first_name) }}
                </FriedParagraph>
              </td>
              <td>
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ getNormalizedCellContent(contact.last_name) }}
                </FriedParagraph>
              </td>
              <td :data-external="`topbar-search-contact:${contact.email}-item`">
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ getNormalizedCellContent(contact.email) }}
                </FriedParagraph>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { ParagraphSize, SkeletonType } from '@getaccept/fried-tofu';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { StatusHelper } from '@getaccept/lib-shared/src/helpers/status.helper';
import { defineComponent, computed, watch } from 'vue';
import type { PropType } from 'vue';
import type {
  SearchResult as ISearchResult,
  HighlightedSearchResultItem,
} from '../types/search-result';
import { SearchResultType } from '../types/enums/search-result-type';
import EmptySearch from '../../../assets/illustrations/empty-search.svg';

export default defineComponent({
  components: {
    EmptySearch,
  },
  props: {
    result: { type: Object as PropType<ISearchResult>, default: null },
    highlightedItem: { type: Object as PropType<HighlightedSearchResultItem>, default: null },
    loading: { type: Boolean },
  },
  emits: ['selectContact', 'selectDocument'],
  setup(props, { emit }) {
    const hasResult = computed(() => !!contacts.value.length || !!documents.value.length);
    const contacts = computed(() => props.result?.contacts || []);
    const documents = computed(() => props.result?.documents || []);

    const isItemHighlighted = (type: SearchResultType, index: number) =>
      props.highlightedItem.type === type && props.highlightedItem.index === index;

    const getNormalizedCellContent = (content: string) => content || '-';

    const selectContact = (contact: User) => emit('selectContact', contact);

    const selectDocument = (document: Document) => emit('selectDocument', document);

    watch(
      () => props.highlightedItem,
      () => watchHighlightedItem(),
      { deep: true }
    );

    const watchHighlightedItem = () => {
      const elementId: string =
        props.result[props.highlightedItem.type][props.highlightedItem.index]?.id;
      if (!elementId) {
        return;
      }
      const highlightedElement: HTMLElement = document.getElementById(elementId);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    return {
      SearchResultType,
      getDocumentHasBadgeBorder: StatusHelper.getDocumentHasBadgeBorder,
      getDocumentStatusTranslation: StatusHelper.getDocumentStatusTranslation,
      getDocumentStatusType: StatusHelper.getDocumentStatusType,
      ParagraphSize,
      SkeletonType,
      hasResult,
      contacts,
      documents,
      isItemHighlighted,
      getNormalizedCellContent,
      selectContact,
      selectDocument,
      watchHighlightedItem,
    };
  },
});
</script>
<style lang="scss" scoped>
$media-query-adjustment: 100px;

.search-result-container {
  position: fixed;
  z-index: 1000;
  top: var(--topbar-padding);
  padding: var(--spacing-100);
  left: 0;
  width: 100%;
  height: 40%;
  background: var(--white);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  box-shadow: 0 5px 4px -4px rgb(0 0 0 / 25%);

  &::after {
    pointer-events: none;
    content: '';
    width: 100%;
    bottom: 0;
    position: fixed;
    left: 0;
    background: linear-gradient(180deg, rgb(255 255 255 / 0%) 0%, rgb(255 255 255 / 60%) 100%);
    height: 3.5rem;
  }

  @media only screen and (max-width: $sm - $media-query-adjustment) {
    height: calc(100% - 100px);
    display: block;
    overflow-y: auto;
  }

  .center {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-50);
  }

  .result-seperator {
    height: 100%;
    margin: 0 var(--spacing-75);
    width: 1px;
    background-color: var(--gray-94);

    @media only screen and (max-width: $sm - $media-query-adjustment) {
      height: 1px;
      width: 100%;
      margin: var(--spacing-75) 0;
    }
  }

  .section-title {
    padding: var(--spacing-50) 0 var(--spacing-100) var(--spacing-50);
  }

  .search-result-table {
    position: relative;
    width: 100%;
    border-collapse: collapse;
    user-select: none;
    table-layout: fixed;
    text-align: left;

    colgroup col {
      &.document-name {
        width: 65%;
      }

      &.contact-first-name {
        width: 20%;

        @media only screen and (max-width: $xxs - 1px) {
          width: 0%;
          visibility: collapse;
        }
      }

      &.contact-last-name {
        width: 30%;

        @media only screen and (max-width: $xxs - 1px) {
          width: 35%;
        }
      }

      &.contact-email {
        width: 50%;

        @media only screen and (max-width: $xxs - 1px) {
          width: 65%;
        }
      }
    }

    tr {
      height: 2.5rem;
    }

    td,
    th {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    thead {
      top: 0;
      position: sticky;
      background: var(--white);
      z-index: 1;

      th {
        color: var(--text-gray);
        padding-left: var(--spacing-50);
      }
    }

    tbody {
      tr {
        border-top: solid 1px var(--gray-89);
        cursor: pointer;

        &:first-child {
          border: 0;
        }

        &:hover,
        &.highlighted {
          background-color: $neutral-300;
        }

        &:active {
          background: var(--gray-89);
        }

        td {
          color: var(--text-gray);
          padding-left: var(--spacing-50);
        }
      }
    }
  }

  .result-contacts,
  .result-documents {
    width: 50%;
    overflow: hidden;
    overflow-y: auto;

    @media only screen and (max-width: $sm - $media-query-adjustment) {
      width: 100%;
      height: auto;
    }
  }
}
</style>
